import React, { useState, useEffect, useRef } from "react";
import Icon from "../components/Icon";
import { useData } from "../data/DataProvider";
import { buildMonths, monthLabel, overlaps, confidenceColor } from "../services/timeline";
import { roleFamily, peopleForRole, buildingColor, BUILDING_TYPES, ROLE_GROUPS, staffGroup, isOpenSeat, OPEN_SEAT } from "../services/roles";
import { isBenchEligible, homeThrough } from "../services/utilization";
import { exportGanttPdf } from "../services/ganttPdf";
import MoveJobWizard from "../components/MoveJobWizard";

const ROLE_OPTIONS = ["Sr. PM", "PM", "APM", "Sr. Supt", "Supt", "Finish Supt", "Asst. Supt", "Gen. Supt", "PE", "FE", "QA/QC", "PX", "Project Accountant", "Estimator", "Precon", "Director", "Admin", "Staff"].sort((a, b) => a.localeCompare(b));
const dateOnly = (d) => (d ? String(d).slice(0, 10) : "");
const shortMDY = (d) => { const s = d ? String(d).slice(0, 10) : ""; const p = s.split("-"); return p.length === 3 ? `${+p[1]}/${+p[2]}/${p[0].slice(2)}` : s; };
const blankJob = () => ({ JobNumber: "", JobName: "", StartDate: "", CompletionDate: "", Status: "Potential", Confidence: 50, BuildingType: "Commercial", team: [] });
const byName = (a, b) => (a.FullName || "").localeCompare(b.FullName || "");

function packLanes(items) {
  const sorted = [...items].sort((a, b) => new Date(a.s) - new Date(b.s));
  const laneEnds = [];
  return sorted.map((it) => {
    let lane = laneEnds.findIndex((end) => new Date(it.s) >= new Date(end));
    if (lane === -1) { lane = laneEnds.length; laneEnds.push(it.e); } else { laneEnds[lane] = it.e; }
    return { ...it, lane };
  });
}

export default function GanttPlanner() {
  const { projects, teamMembers, people, addProject, editProject, addTeamMember, moveJobAndCrew } = useData();

  const allProjects = projects || [];
  const activeProjects = allProjects.filter(p => (p.Status || "Active") === "Active" && p.StartDate && p.CompletionDate);
  const potentialProjects = allProjects.filter(p => p.Status === "Potential" && p.StartDate && p.CompletionDate);
  const allPeople = (people || []).filter(p => p.IsActive !== false);
  const sortedPeople = [...allPeople].sort(byName);
  const allMembers = teamMembers || [];

  const [view, setView] = useState("project");
  const [enabledProspects, setEnabledProspects] = useState({});
  const [prospectsTouched, setProspectsTouched] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newJob, setNewJob] = useState(blankJob());
  const [expanded, setExpanded] = useState({});
  const [expandAll, setExpandAll] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState("");
  const [exporting, setExporting] = useState(false);
  const [showStaff, setShowStaff] = useState(false);
  const [staff, setStaff] = useState({ JobNumber: "", PersonName: "", ProjectRole: "PM", AllocationPercent: 100 });
  const [confDraft, setConfDraft] = useState({}); // pipeline confidence being edited
  const ganttScrollRef = useRef(null);
  // Presenter mode: the Gantt fills the screen (browser fullscreen when allowed).
  const [presenting, setPresenting] = useState(false);
  const [moveJob, setMoveJob] = useState(null); // job being rescheduled in the move wizard
  const presentRef = useRef(null);
  const [winPreset, setWinPreset] = useState("default");
  // By-person filters
  const [onlyNoHome, setOnlyNoHome] = useState(false); // show only people whose work runs out < 90 days
  const [filterGroups, setFilterGroups] = useState(new Set());
  const [filterPeople, setFilterPeople] = useState(new Set());

  const toggleProspect = (id) => { setProspectsTouched(true); setEnabledProspects(prev => ({ ...prev, [id]: !prev[id] })); };
  const toggleExpand = (title) => setExpanded(prev => ({ ...prev, [title]: !prev[title] }));
  // Open (unfilled) seats on a job, and a "1 PM, 1 Supt" style summary.
  const hasOpenSeats = (title) => (teamMembers || []).some(m => m.ProjectJobNumber === title && isOpenSeat(m));
  const openSeatSummary = (title) => {
    const seats = (teamMembers || []).filter(m => m.ProjectJobNumber === title && isOpenSeat(m));
    if (!seats.length) return "";
    const byRole = {};
    seats.forEach(s => { byRole[s.ProjectRole] = (byRole[s.ProjectRole] || 0) + 1; });
    return Object.entries(byRole).map(([r, n]) => `${n} ${r}`).join(", ");
  };
  // Same data as a list, so the open positions can be rendered as role chips.
  const openSeatRoles = (title) => {
    const seats = (teamMembers || []).filter(m => m.ProjectJobNumber === title && isOpenSeat(m));
    const byRole = {};
    seats.forEach(s => {
      const r = s.ProjectRole || "Unspecified";
      byRole[r] = (byRole[r] || 0) + 1;
    });
    return Object.entries(byRole).map(([role, count]) => ({ role, count }));
  };
  // Jobs with unfilled seats default to expanded so VPs see the open roles on the
  // Gantt without clicking; once a user toggles a job, their choice wins.
  const isOpen = (title) => expandAll || (title in expanded ? !!expanded[title] : hasOpenSeats(title));
  const toggleGroup = (k) => setFilterGroups(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const removePerson = (name) => setFilterPeople(prev => { const n = new Set(prev); n.delete(name); return n; });

  const projType = (title) => allProjects.find(p => p.Title === title)?.BuildingType;
  const projColor = (title) => buildingColor(projType(title));

  const prospects = potentialProjects.map(p => ({
    Id: p.Id, Title: p.Title, JobName: p.JobName || p.Title,
    StartDate: p.StartDate, CompletionDate: p.CompletionDate,
    Confidence: Number(p.Confidence) || 50,
    RolesNeeded: allMembers.filter(m => m.ProjectJobNumber === p.Title && isOpenSeat(m)).map(m => m.ProjectRole),
  }));
  const enableAll = () => { setProspectsTouched(true); const all = {}; prospects.forEach(p => { all[p.Id] = true; }); setEnabledProspects(all); };
  const activeProspects = prospects.filter(p => enabledProspects[p.Id]);

  // Default: all pipeline (Potential) jobs are turned on until the user toggles.
  const potentialIds = potentialProjects.map(p => p.Id).join(",");
  useEffect(() => {
    if (!prospectsTouched && potentialProjects.length) {
      setEnabledProspects(Object.fromEntries(potentialProjects.map(p => [p.Id, true])));
    }
  }, [potentialIds, prospectsTouched]);

  // ── Timeline window (default: last 12 months → next 18 months) ──
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const addMonths = (d, n) => { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; };
  const toInput = (d) => { try { return new Date(d).toISOString().slice(0, 10); } catch { return ""; } };
  const allProjDates = allProjects.flatMap(p => [p.StartDate, p.CompletionDate].filter(Boolean).map(d => new Date(d)));
  const dataMin = allProjDates.length ? new Date(Math.min(...allProjDates)) : today;
  const dataMax = allProjDates.length ? new Date(Math.max(...allProjDates)) : addMonths(today, 18);

  // Default view: 3 months back → 1 year out.
  const [winStart, setWinStart] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); d.setMonth(d.getMonth() - 3); d.setDate(1); return d; });
  const [winEnd, setWinEnd] = useState(() => addMonths(new Date(new Date().setHours(0, 0, 0, 0)), 12));

  const timelineStart = new Date(winStart); timelineStart.setDate(1);
  const timelineEnd = new Date(winEnd);
  const months = buildMonths(timelineStart, timelineEnd);

  // Window presets (one dropdown instead of a row of buttons).
  const applyPreset = (key) => {
    setWinPreset(key);
    if (key === "default") { const s = new Date(today); s.setMonth(s.getMonth() - 3); s.setDate(1); setWinStart(s); setWinEnd(addMonths(today, 12)); }
    if (key === "year") { setWinStart(addMonths(today, -12)); setWinEnd(addMonths(today, 18)); }
    if (key === "two") { setWinStart(addMonths(today, -24)); setWinEnd(addMonths(today, 24)); }
    if (key === "all") { setWinStart(dataMin); setWinEnd(dataMax); }
  };

  // Presenter mode. Esc (or leaving browser fullscreen) exits.
  const startPresenting = () => {
    setPresenting(true);
    if (ganttScrollRef.current) ganttScrollRef.current.scrollTop = 0;
    const el = presentRef.current;
    if (el && el.requestFullscreen) el.requestFullscreen().catch(() => {}); // overlay still works without it
  };
  const stopPresenting = () => {
    setPresenting(false);
    if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(() => {});
  };
  useEffect(() => {
    if (!presenting) return;
    const onFs = () => { if (!document.fullscreenElement) setPresenting(false); };
    const onKey = (e) => { if (e.key === "Escape") setPresenting(false); };
    document.addEventListener("fullscreenchange", onFs);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("fullscreenchange", onFs); document.removeEventListener("keydown", onKey); };
  }, [presenting]);

  // Map a date to a percentage across the EQUAL-WIDTH month columns, so bars and
  // the data-date line line up exactly with the month grid/labels. (A linear-time
  // scale drifts off the columns because months have different day counts.)
  const trackPct = (date) => {
    const d = new Date(date);
    const idx = (d.getFullYear() - timelineStart.getFullYear()) * 12 + (d.getMonth() - timelineStart.getMonth());
    const dim = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    const pos = (idx + (d.getDate() - 1) / dim) / months.length;
    return Math.max(0, Math.min(1, pos)) * 100;
  };

  // Clamp a bar to the visible window; null if it falls entirely outside.
  const posOf = (s, e) => {
    if (!s || !e) return null;
    const a = new Date(s), b = new Date(e);
    if (b < timelineStart || a > timelineEnd) return null;
    const left = trackPct(a);
    const right = trackPct(b);
    return { left, width: Math.max(1.2, right - left) };
  };

  // Today / data-date marker, on the same month-column scale as the bars.
  const todayInWindow = today >= timelineStart && today <= timelineEnd;
  const todayTrackPct = todayInWindow ? trackPct(today) : null;
  const todayLabel = today.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const ganttInnerW = 270 + months.length * 52;
  const GANTT_LABEL_W = 250; // row-label width (border-box, fixed across all rows)
  const todayLeftPx = todayTrackPct != null ? GANTT_LABEL_W + (todayTrackPct / 100) * (ganttInnerW - GANTT_LABEL_W) : null;
  useEffect(() => {
    const el = ganttScrollRef.current;
    if (!el || todayLeftPx == null) return;
    el.scrollLeft = Math.max(0, todayLeftPx - el.clientWidth / 2);
  }, [todayLeftPx, view, months.length]);

  // ── Who's actually available ──────────────────────────────────────────────
  // Time-aware, matching the rest of the app: someone is available unless they
  // hold an assignment on an ACTIVE job that still runs today. (The old version
  // treated anyone who had ever appeared on any assignment as permanently
  // deployed, so the bench always read 0.)
  const activeTitleSet = new Set(activeProjects.map(p => p.Title));
  const committedNow = new Set();
  allMembers.forEach(m => {
    if (isOpenSeat(m) || !activeTitleSet.has(m.ProjectJobNumber)) return;
    const end = m.EndDate ? new Date(m.EndDate) : null;
    if (!end || isNaN(end) || end >= today) committedNow.add(m.PersonName);
  });
  const availableByFamily = {};
  allPeople.forEach(p => {
    if (!isBenchEligible(p) || committedNow.has(p.FullName)) return;
    const fam = roleFamily(p.CompanyRole);
    availableByFamily[fam] = (availableByFamily[fam] || 0) + 1;
  });

  // ── What the enabled pipeline jobs need ───────────────────────────────────
  // Grouped by role FAMILY so a Sr. Supt can cover a Supt seat (exact-string
  // matching never lined up, which is why every card used to say "0 on bench").
  const FAMILY_LABEL = Object.fromEntries(ROLE_GROUPS.map(g => [g.key, g.label]));
  const demandByFamily = {};
  activeProspects.forEach(p => {
    (p.RolesNeeded || []).forEach(seatRole => {
      const fam = roleFamily(seatRole);
      const d = demandByFamily[fam] = demandByFamily[fam] || { need: 0, roles: {} };
      d.need += 1;
      d.roles[seatRole] = (d.roles[seatRole] || 0) + 1;
    });
  });
  const shortfalls = Object.entries(demandByFamily).map(([fam, d]) => {
    const have = availableByFamily[fam] || 0;
    return {
      family: fam,
      label: FAMILY_LABEL[fam] || "Other",
      need: d.need,
      detail: Object.entries(d.roles).map(([r, n]) => `${n} ${r}`).join(", "),
      have,
      gap: Math.max(0, d.need - have),
    };
  }).sort((a, b) => b.gap - a.gap || b.need - a.need);

  // Only pipeline jobs that actually declared seats contribute to the numbers.
  const jobsWithNeeds = activeProspects.filter(p => (p.RolesNeeded || []).length > 0).length;

  const overlapWarnings = [];
  for (let i = 0; i < activeProspects.length; i++) {
    for (let j = i + 1; j < activeProspects.length; j++) {
      const a = activeProspects[i], b = activeProspects[j];
      if (overlaps(new Date(a.StartDate), new Date(a.CompletionDate), new Date(b.StartDate), new Date(b.CompletionDate))) overlapWarnings.push([a.JobName, b.JobName]);
    }
  }
  const totalNewRoles = shortfalls.reduce((a, s) => a + s.need, 0);

  // ── Add-job team selector (role-family filtered) ──
  const peopleForRow = (t) => {
    const fam = roleFamily(t.ProjectRole);
    return sortedPeople.filter(p => (roleFamily(p.CompanyRole) === fam && !newJob.team.some(x => x.PersonName === p.FullName)) || p.FullName === t.PersonName);
  };
  const addTeamRow = () => {
    const candidate = sortedPeople.find(p => roleFamily(p.CompanyRole) === "pm" && !newJob.team.some(t => t.PersonName === p.FullName))
      || sortedPeople.find(p => !newJob.team.some(t => t.PersonName === p.FullName));
    if (!candidate) return;
    setNewJob(prev => ({ ...prev, team: [...prev.team, { PersonName: candidate.FullName, ProjectRole: candidate.CompanyRole || "PM", AllocationPercent: 100 }] }));
  };
  const addOpenSeatRow = () => setNewJob(prev => ({ ...prev, team: [...prev.team, { PersonName: OPEN_SEAT, ProjectRole: "PM", AllocationPercent: 100 }] }));
  const updateTeamRow = (i, patch) => setNewJob(prev => ({
    ...prev,
    team: prev.team.map((t, idx) => {
      if (idx !== i) return t;
      const next = { ...t, ...patch };
      // If role changed and the person no longer matches the family, clear them.
      if (patch.ProjectRole && roleFamily(next.ProjectRole) !== roleFamily(allPeople.find(p => p.FullName === next.PersonName)?.CompanyRole)) {
        next.PersonName = "";
      }
      return next;
    }),
  }));
  const removeTeamRow = (i) => setNewJob(prev => ({ ...prev, team: prev.team.filter((_, idx) => idx !== i) }));

  const activeNeedsStart = newJob.Status === "Active" && !newJob.StartDate;
  const canSave = (newJob.JobNumber.trim() || newJob.Status === "Potential") && newJob.JobName && newJob.CompletionDate && !activeNeedsStart && !saving;

  const addJob = async () => {
    if (!canSave) return;
    // Pipeline jobs may not have a real number yet — assign a temporary one.
    const jobNumber = newJob.JobNumber.trim() || (newJob.Status === "Potential" ? "PIPE-" + Date.now().toString().slice(-6) : "");
    if (!jobNumber) return;
    setSaving(true); setSaveErr("");
    try {
      const fields = {
        Title: jobNumber, JobNumber: jobNumber, JobName: newJob.JobName,
        StartDate: newJob.StartDate ? new Date(newJob.StartDate).toISOString() : null,
        CompletionDate: newJob.CompletionDate ? new Date(newJob.CompletionDate).toISOString() : null,
        Status: newJob.Status, BuildingType: newJob.BuildingType,
      };
      if (newJob.Status === "Potential") fields.Confidence = newJob.Confidence;
      await addProject(fields);
      for (const t of newJob.team) {
        if (!t.PersonName) continue;
        await addTeamMember({
          ProjectJobNumber: jobNumber, PersonName: t.PersonName, ProjectRole: t.ProjectRole,
          AllocationPercent: t.AllocationPercent,
          StartDate: newJob.StartDate ? new Date(newJob.StartDate).toISOString() : null,
          EndDate: newJob.CompletionDate ? new Date(newJob.CompletionDate).toISOString() : null,
        });
      }
      setNewJob(blankJob());
      setShowAddForm(false);
    } catch (e) { setSaveErr(e.message || "Couldn't save the job."); }
    finally { setSaving(false); }
  };

  // ── Inline pipeline staffing (role-family filtered) ──
  // Dropdown lists are alphabetical by job name.
  const pipelineSorted = [...potentialProjects].sort((a, b) => (a.JobName || a.Title || "").localeCompare(b.JobName || b.Title || ""));
  const staffProject = potentialProjects.find(p => p.Title === staff.JobNumber) || null;
  const staffablePeople = peopleForRole(sortedPeople, staff.ProjectRole)
    .filter(p => !allMembers.some(m => m.ProjectJobNumber === staff.JobNumber && m.PersonName === p.FullName));
  const saveStaff = async () => {
    if (!staff.JobNumber || !staff.PersonName) return;
    setSaving(true); setSaveErr("");
    try {
      await addTeamMember({
        ProjectJobNumber: staff.JobNumber, PersonName: staff.PersonName, ProjectRole: staff.ProjectRole,
        AllocationPercent: staff.AllocationPercent,
        StartDate: staffProject?.StartDate || null, EndDate: staffProject?.CompletionDate || null,
      });
      setStaff({ ...staff, PersonName: "" });
    } catch (e) { setSaveErr(e.message || "Couldn't staff that person."); }
    finally { setSaving(false); }
  };
  const addOpenSeatToPipeline = async () => {
    if (!staff.JobNumber) return;
    setSaving(true); setSaveErr("");
    try {
      await addTeamMember({
        ProjectJobNumber: staff.JobNumber, PersonName: OPEN_SEAT, ProjectRole: staff.ProjectRole,
        AllocationPercent: 100, StartDate: staffProject?.StartDate || null, EndDate: staffProject?.CompletionDate || null,
      });
    } catch (e) { setSaveErr(e.message || "Couldn't add seat."); }
    finally { setSaving(false); }
  };

  // ── By-person rows ──
  const visibleTitles = new Set([...activeProjects.map(p => p.Title), ...activeProspects.map(p => p.Title)]);
  const buildPersonItems = (person) => allMembers
    .filter(m => m.PersonName === person.FullName && visibleTitles.has(m.ProjectJobNumber))
    .map(m => {
      const proj = allProjects.find(p => p.Title === m.ProjectJobNumber);
      const potential = proj?.Status === "Potential";
      const s = m.StartDate ? new Date(m.StartDate) : new Date(proj?.StartDate || timelineStart);
      const e = m.EndDate ? new Date(m.EndDate) : new Date(proj?.CompletionDate || timelineEnd);
      return { title: m.ProjectJobNumber, jobName: proj?.JobName || m.ProjectJobNumber, role: m.ProjectRole, alloc: m.AllocationPercent, potential, s, e };
    });
  const peopleWithWork = sortedPeople
    .map(person => ({ person, items: packLanes(buildPersonItems(person)) }))
    .filter(x => x.items.length > 0);

  // Active jobs with nobody actually assigned (only open seats, or no team at
  // all) — surfaced as their own blocks in the By-Person view so the gap shows.
  // A job counts as unstaffed when nobody real is on it — open seats don't count
  // as staffing. Covers active jobs AND the pipeline jobs toggled on, since a
  // prospective job with only open seats is exactly the gap to plan for.
  const filledTitles = new Set((allMembers || []).filter(m => !isOpenSeat(m)).map(m => m.ProjectJobNumber));
  const unstaffedJobs = [
    ...activeProjects.filter(p => !filledTitles.has(p.Title)),
    ...activeProspects
      .filter(ap => !filledTitles.has(ap.Title))
      .map(ap => allProjects.find(x => x.Title === ap.Title) || ap),
  ];

  // ── "No home in 90 days" forecast ──
  // A person has a home through the horizon if any of their filled assignments —
  // active OR prospective, across ALL jobs (not just the toggled-on ones) — reaches
  // the 90-day mark. If everything they're on wraps up before then (or they have
  // nothing), they need a next home.
  const HORIZON = new Date(today.getTime() + 90 * 86400000);
  const homeInfo = (fullName) => homeThrough(fullName, teamMembers, allProjects, HORIZON);
  const noHomePeople = sortedPeople
    .filter(p => isBenchEligible(p) && !homeInfo(p.FullName).covered)
    .map(person => ({ person, items: packLanes(buildPersonItems(person)) }));
  // Bench-eligibility is decided from CompanyRole, so anyone without one is
  // invisible to this forecast. Say so rather than quietly leaving them out.
  const unclassified = sortedPeople.filter(p => !(p.CompanyRole || "").trim());

  const personBase = onlyNoHome ? noHomePeople : peopleWithWork;
  // Operations-only "plug and play" groups: PM (incl. PEs), Superintendents
  // (incl. FEs), QA/QC, and a catch-all Other. Support/leadership are omitted.
  const OPS_GROUP_KEYS = new Set(["pm", "supt", "qaqc", "other"]);
  const presentGroups = ROLE_GROUPS.filter(g => OPS_GROUP_KEYS.has(g.key) && peopleWithWork.some(x => roleFamily(x.person.CompanyRole) === g.key));
  const noFilter = filterGroups.size === 0 && filterPeople.size === 0;
  const shownPeople = personBase.filter(x => noFilter || filterGroups.has(roleFamily(x.person.CompanyRole)) || filterPeople.has(x.person.FullName));
  const pickablePeople = peopleWithWork.filter(x => !filterPeople.has(x.person.FullName));

  // ── Export (vector PDF) ──
  const handleExport = async () => {
    setExporting(true);
    try {
      const legend = [...BUILDING_TYPES.map(t => ({ label: t, color: buildingColor(t) })), { label: "Pipeline (potential)", color: "#8a9099", hatched: true }];
      const rows = [];
      const pushProject = (p, hatched) => {
        const mem = allMembers.filter(m => m.ProjectJobNumber === p.Title);
        rows.push({ kind: "project", label: (p.JobName || p.Title) + (hatched ? " — PIPELINE" : ""), meta: `${p.Title} · ${p.BuildingType || "—"} · ${mem.length} staffed`, accent: buildingColor(p.BuildingType), bars: [{ start: p.StartDate, end: p.CompletionDate, color: buildingColor(p.BuildingType), label: p.JobName || p.Title, hatched }] });
        const groups = {};
        mem.forEach(m => { const g = staffGroup(m.ProjectRole); (groups[g.key] = groups[g.key] || { g, items: [] }).items.push(m); });
        Object.values(groups).sort((a, b) => a.g.order - b.g.order).forEach(({ g, items }) => {
          rows.push({ kind: "group", label: `${g.label} · ${items.length}`, color: g.color });
          items.forEach(m => { const open = isOpenSeat(m); const nm = open ? "Open · " + m.ProjectRole : m.PersonName; rows.push({ kind: "member", label: nm, meta: `${m.ProjectRole}${m.AllocationPercent != null ? " · " + m.AllocationPercent + "%" : ""}${m.EndDate ? " · ends " + dateOnly(m.EndDate) : ""}`, bars: [{ start: m.StartDate || p.StartDate, end: m.EndDate || p.CompletionDate, color: g.color, label: nm, hatched: hatched || open }] }); });
        });
      };
      if (view === "project") {
        activeProjects.forEach(p => pushProject(p, false));
        activeProspects.forEach(ap => pushProject(allProjects.find(x => x.Title === ap.Title) || ap, true));
      } else {
        shownPeople.forEach(({ person, items }) => {
          const maxLane = Math.max(0, ...items.map(it => it.lane));
          rows.push({
            kind: "person", label: person.FullName, meta: `${person.CompanyRole} · ${items.length} job${items.length === 1 ? "" : "s"}`, extraLanes: maxLane,
            bars: items.map(it => ({ start: it.s, end: it.e, color: projColor(it.title), label: it.jobName, hatched: it.potential, lane: it.lane })),
          });
        });
      }
      await exportGanttPdf({
        title: "Hardaway — Staffing Gantt",
        subtitle: `${view === "project" ? "By project" : "By person"} · generated ${new Date().toLocaleDateString()}`,
        rows, monthStarts: months, tStart: timelineStart, tEnd: timelineEnd, legend,
        fileName: `gantt-${view}-${new Date().toISOString().slice(0, 10)}.pdf`,
      });
    } catch (e) {
      setSaveErr("PDF export needs the jspdf package installed (run: npm install jspdf).");
    } finally { setExporting(false); }
  };

  // Project view: members grouped by staff group (PM Staff, Field Staff, QA/QC…),
  // each bar colored by its group.
  const memberRows = (jobTitle, jobStart, jobEnd) => {
    const members = allMembers.filter(m => m.ProjectJobNumber === jobTitle);
    if (members.length === 0) {
      return <div className="gantt-row gantt-subrow"><div className="gantt-row-label" style={{ paddingLeft: 18, color: "var(--text-muted)", fontSize: 12 }}>No one staffed yet</div><div className="gantt-track-area" /></div>;
    }
    const groups = {};
    members.forEach(m => { const g = staffGroup(m.ProjectRole); (groups[g.key] = groups[g.key] || { g, items: [] }).items.push(m); });
    const ordered = Object.values(groups).sort((a, b) => a.g.order - b.g.order);
    const out = [];
    ordered.forEach(({ g, items }) => {
      out.push(
        <div key={"grp-" + jobTitle + "-" + g.key} className="gantt-row gantt-subrow">
          <div className="gantt-row-label" style={{ paddingLeft: 18 }}>
            <div className="gantt-row-meta" style={{ fontWeight: 800, color: g.color, textTransform: "uppercase", letterSpacing: "0.5px" }}>{g.label} · {items.length}</div>
          </div>
          <div className="gantt-track-area" />
        </div>
      );
      items.forEach((m, i) => {
        const ms = m.StartDate ? new Date(m.StartDate) : (jobStart ? new Date(jobStart) : timelineStart);
        const me = m.EndDate ? new Date(m.EndDate) : (jobEnd ? new Date(jobEnd) : timelineEnd);
        const mpos = posOf(ms, me);
        if (!mpos) return;
        const open = isOpenSeat(m);
        const nm = open ? `Open · ${m.ProjectRole}` : m.PersonName;
        const barBg = open ? `repeating-linear-gradient(45deg, ${g.color}, ${g.color} 6px, rgba(255,255,255,0.5) 6px, rgba(255,255,255,0.5) 12px)` : g.color;
        // Show the assignment window as distinct green (Start) / red (End) pills
        // so prospective, future-starting staff are easy to read at a glance.
        const startStr = m.StartDate ? dateOnly(m.StartDate) : null;
        const endStr = m.EndDate ? dateOnly(m.EndDate) : null;
        out.push(
          <div key={(m.Id || i) + "-" + g.key} className="gantt-row gantt-subrow">
            <div className="gantt-row-label" style={{ paddingLeft: 30 }}>
              <div className="gantt-row-name" style={{ fontSize: 12, fontStyle: open ? "italic" : "normal", color: open ? "var(--gold-text)" : undefined }}>{nm}</div>
              <div className="gantt-row-meta" style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "nowrap", whiteSpace: "nowrap" }}>
                {open
                  ? <span style={{ fontSize: 9, fontWeight: 800, color: "var(--gold-text)", background: "var(--gold-soft)", border: "1px dashed var(--gold)", borderRadius: 6, padding: "1px 6px", letterSpacing: "0.5px" }}>UNFILLED</span>
                  : <span>{m.ProjectRole}{m.AllocationPercent != null ? ` · ${m.AllocationPercent}%` : ""}</span>}
                {startStr && <span className="date-pill start" title={`Start ${startStr}`}><span className="date-pill-dot" />{shortMDY(m.StartDate)}</span>}
                {endStr && <span className="date-pill end" title={`End ${endStr}`}><span className="date-pill-dot" />{shortMDY(m.EndDate)}</span>}
              </div>
            </div>
            <div className="gantt-track-area">
              {months.map((_, j) => <div key={j} className="gantt-gridline" style={{ left: `${(j / months.length) * 100}%` }} />)}
              <div className="gantt-bar" style={{ left: `${mpos.left}%`, width: `${mpos.width}%`, background: barBg, height: 16, top: "calc(50% - 8px)" }} title={`${nm}: ${dateOnly(ms)} → ${dateOnly(me)}`}>
                <span className={`gantt-bar-label${open ? " on-hatch" : ""}`} style={{ fontSize: 10 }}>{nm}</span>
              </div>
            </div>
          </div>
        );
      });
    });
    return out;
  };

  return (
    <div>
      {/* One toolbar: view + window on the left, actions on the right. */}
      <div className="gantt-toolbar">
        <div className="gantt-toolbar-group">
          <div className="seg">
            <button className={view === "project" ? "on" : ""} onClick={() => setView("project")}>By Project</button>
            <button className={view === "person" ? "on" : ""} onClick={() => setView("person")}>By Person</button>
          </div>
          <label className="gantt-toolbar-field">
            <Icon name="calendar" size={14} />
            <select className="form-select" value={winPreset} onChange={e => applyPreset(e.target.value)} aria-label="Timeline window">
              <option value="all">All time</option>
              <option value="custom">Custom dates…</option>
              <option value="two">Last 2 years → next 2 years</option>
              <option value="default">Last 3 months → next year</option>
              <option value="year">Last year → next 18 months</option>
            </select>
          </label>
          {winPreset === "custom" && (
            <span className="gantt-toolbar-dates">
              <input className="form-input" type="date" value={toInput(winStart)} onChange={e => { if (e.target.value) setWinStart(new Date(e.target.value)); }} aria-label="From" />
              <span>→</span>
              <input className="form-input" type="date" value={toInput(winEnd)} onChange={e => { if (e.target.value) setWinEnd(new Date(e.target.value)); }} aria-label="To" />
            </span>
          )}
        </div>
        <div className="gantt-toolbar-group">
          <button className="btn btn-ghost" onClick={startPresenting} title="Show the timeline full screen for meetings"><Icon name="maximize" size={14} /> Present</button>
          <button className="btn btn-ghost" onClick={handleExport} disabled={exporting}><Icon name="file-text" size={14} /> {exporting ? "Exporting…" : "Export PDF"}</button>
          {potentialProjects.length > 0 && <button className={`btn ${showStaff ? "btn-secondary" : "btn-ghost"}`} onClick={() => setShowStaff(s => !s)}><Icon name="user-plus" size={14} /> Staff pipeline</button>}
          <button className="btn btn-primary" onClick={() => setShowAddForm(s => !s)}><Icon name="plus" size={14} /> Add Job</button>
        </div>
      </div>

      {saveErr && <div className="card mb-16" style={{ borderColor: "var(--red)", color: "var(--red)", fontSize: 13 }}>{saveErr}</div>}

      {/* By-person filter */}
      {view === "person" && (
        <div className="card mb-16" style={{ padding: "12px 14px" }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <button className={`btn ${onlyNoHome ? "btn-primary" : "btn-ghost"}`} style={{ fontSize: 12, padding: "4px 12px" }} title="Show only people whose current + prospective work runs out within 90 days" onClick={() => setOnlyNoHome(v => !v)}><Icon name="alert" size={12} /> No home &lt; 90 days</button>
            <span className="gantt-filter-divider" />
            <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginRight: 4 }}>Groups</span>
            {presentGroups.map(g => (
              <button key={g.key} onClick={() => toggleGroup(g.key)} className={`btn ${filterGroups.has(g.key) ? "btn-primary" : "btn-ghost"}`} style={{ fontSize: 12, padding: "4px 12px" }}>{g.label}</button>
            ))}
            <select className="form-select" style={{ maxWidth: 220, marginLeft: "auto" }} value="" onChange={e => { if (e.target.value) setFilterPeople(prev => new Set(prev).add(e.target.value)); }}>
              <option value="">+ Add a person…</option>
              {pickablePeople.map(({ person }) => <option key={person.Id} value={person.FullName}>{person.FullName}</option>)}
            </select>
          </div>
          {(filterPeople.size > 0 || filterGroups.size > 0) && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10, alignItems: "center" }}>
              {[...filterPeople].map(n => (
                <span key={n} style={{ fontSize: 12, background: "var(--bg-elevated)", borderRadius: 999, padding: "3px 8px 3px 10px", display: "inline-flex", alignItems: "center", gap: 6 }}>
                  {n}<button onClick={() => removePerson(n)} style={{ border: "none", background: "none", cursor: "pointer", color: "var(--red)", fontSize: 13 }}>×</button>
                </span>
              ))}
              <button className="btn btn-ghost" style={{ fontSize: 11, padding: "3px 10px" }} onClick={() => { setFilterGroups(new Set()); setFilterPeople(new Set()); }}>Clear filters</button>
            </div>
          )}
        </div>
      )}

      {showStaff && (
        <div className="card mb-24">
          <div className="card-title mb-16">Staff a Pipeline Job</div>
          {potentialProjects.length === 0 ? (
            <div style={{ fontSize: 13, color: "var(--text-muted)" }}>No pipeline (Potential) jobs yet.</div>
          ) : (
            <>
            <div className="staff-form-grid">
              <div>
                <label className="form-label">Pipeline Job</label>
                <select className="form-select" value={staff.JobNumber} onChange={e => setStaff({ ...staff, JobNumber: e.target.value, PersonName: "" })}>
                  <option value="">Select…</option>
                  {pipelineSorted.map(p => <option key={p.Id} value={p.Title}>{p.JobName || p.Title} ({p.Confidence != null ? p.Confidence + "%" : "—"})</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Role</label>
                <select className="form-select" value={staff.ProjectRole} onChange={e => setStaff({ ...staff, ProjectRole: e.target.value, PersonName: "" })}>
                  {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Person ({staff.ProjectRole})</label>
                <select className="form-select" value={staff.PersonName} onChange={e => setStaff({ ...staff, PersonName: e.target.value })} disabled={!staff.JobNumber}>
                  <option value="">Select…</option>
                  {staffablePeople.map(p => <option key={p.Id} value={p.FullName}>{p.FullName} — {p.CompanyRole}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Util %</label>
                <input className="form-input" type="number" min="0" max="100" value={staff.AllocationPercent} onChange={e => setStaff({ ...staff, AllocationPercent: parseInt(e.target.value) || 0 })} />
              </div>
              <button className="btn btn-primary" onClick={saveStaff} disabled={!staff.JobNumber || !staff.PersonName || saving}>{saving ? "…" : "Assign"}</button>
            </div>
            <div style={{ marginTop: 12, fontSize: 12, color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              Or declare a needed role with nobody in it yet:
              <button className="btn btn-secondary" style={{ fontSize: 12, padding: "4px 12px" }} onClick={addOpenSeatToPipeline} disabled={!staff.JobNumber || saving}><Icon name="plus" size={12} style={{ verticalAlign: "middle", marginRight: 4 }} /> Add open {staff.ProjectRole} seat to this job</button>
            </div>
            <div style={{ marginTop: 18, borderTop: "1px solid var(--border)", paddingTop: 14 }}>
              <div className="form-label" style={{ marginBottom: 8 }}>Win confidence (drag to update)</div>
              {pipelineSorted.map(p => {
                const val = confDraft[p.Id] != null ? confDraft[p.Id] : (p.Confidence != null ? Number(p.Confidence) : 50);
                return (
                  <div key={p.Id} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
                    <span style={{ flex: 1, fontSize: 13 }}>{p.JobName || p.Title}</span>
                    <input type="range" min="5" max="100" step="5" value={val} onChange={e => setConfDraft(d => ({ ...d, [p.Id]: parseInt(e.target.value) }))} onMouseUp={() => editProject(p.Id, { Confidence: val })} onTouchEnd={() => editProject(p.Id, { Confidence: val })} style={{ width: 180, accentColor: "var(--gold)" }} />
                    <span style={{ width: 42, textAlign: "right", fontWeight: 700, color: "var(--gold-text)" }}>{val}%</span>
                  </div>
                );
              })}
            </div>
            </>
          )}
        </div>
      )}

      {showAddForm && (
        <div className="card mb-24">
          <div className="card-title mb-16">Add Job</div>
          <div className="addjob-grid-a">
            <div><label className="form-label">Job Number {newJob.Status === "Potential" ? "(optional)" : "*"}</label><input className="form-input" value={newJob.JobNumber} onChange={e => setNewJob({ ...newJob, JobNumber: e.target.value })} placeholder={newJob.Status === "Potential" ? "auto-assigned if blank" : "e.g. 2426"} /></div>
            <div><label className="form-label">Job Name</label><input className="form-input" value={newJob.JobName} onChange={e => setNewJob({ ...newJob, JobName: e.target.value })} placeholder="e.g. Downtown Office Tower" /></div>
            <div>
              <label className="form-label">Status</label>
              <select className="form-select" value={newJob.Status} onChange={e => setNewJob({ ...newJob, Status: e.target.value })}>
                <option value="Active">Active (committed)</option>
                <option value="Potential">Potential (pipeline)</option>
              </select>
            </div>
            <div><label className="form-label">{newJob.Status === "Active" ? "Confirmed Start *" : "Expected Start"}</label><input className="form-input" type="date" value={newJob.StartDate} onChange={e => setNewJob({ ...newJob, StartDate: e.target.value })} /></div>
          </div>

          <div className="addjob-grid-b">
            <div><label className="form-label">Completion *</label><input className="form-input" type="date" value={newJob.CompletionDate} onChange={e => setNewJob({ ...newJob, CompletionDate: e.target.value })} /></div>
            <div>
              <label className="form-label">Building Type</label>
              <select className="form-select" value={newJob.BuildingType} onChange={e => setNewJob({ ...newJob, BuildingType: e.target.value })}>
                {BUILDING_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            {newJob.Status === "Potential" && (
              <div><label className="form-label">Win Confidence: {newJob.Confidence}%</label><input type="range" min="5" max="100" step="5" value={newJob.Confidence} onChange={e => setNewJob({ ...newJob, Confidence: parseInt(e.target.value) })} style={{ width: "100%", accentColor: "var(--gold)" }} /></div>
            )}
          </div>

          {newJob.Status === "Active" && (
            <div style={{ fontSize: 12, color: activeNeedsStart ? "var(--red)" : "var(--text-muted)", marginTop: 8 }}>
              <Icon name="alert" size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />
              Active jobs require a confirmed start date. Use <b>Potential</b> for work you can staff but haven't committed to.
            </div>
          )}

          <div style={{ marginTop: 18 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <label className="form-label" style={{ margin: 0 }}>Assign Team / Seats ({newJob.team.length})</label>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-secondary" style={{ fontSize: 12, padding: "4px 10px" }} onClick={addOpenSeatRow}><Icon name="plus" size={12} style={{ verticalAlign: "middle", marginRight: 4 }} /> Add open seat</button>
                <button className="btn btn-ghost" style={{ fontSize: 12, padding: "4px 10px" }} onClick={addTeamRow}><Icon name="plus" size={12} style={{ verticalAlign: "middle", marginRight: 4 }} /> Add person</button>
              </div>
            </div>
            {newJob.team.length === 0 && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Pick a role first — the person list filters to people in that role family.</div>}
            {newJob.team.map((t, i) => (
              <div key={i} className="team-row-grid">
                <select className="form-select" value={t.ProjectRole} onChange={e => updateTeamRow(i, { ProjectRole: e.target.value })}>
                  {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <select className="form-select" value={t.PersonName} onChange={e => updateTeamRow(i, { PersonName: e.target.value })}>
                  <option value="">Select {t.ProjectRole}…</option>
                  <option value={OPEN_SEAT}>— Open seat (no person yet) —</option>
                  {peopleForRow(t).map(p => <option key={p.Id} value={p.FullName}>{p.FullName} — {p.CompanyRole}</option>)}
                </select>
                <input className="form-input" type="number" min="0" max="100" value={t.AllocationPercent} onChange={e => updateTeamRow(i, { AllocationPercent: parseInt(e.target.value) || 0 })} title="Utilization %" />
                <button className="btn btn-ghost" style={{ padding: "4px 10px", color: "var(--red)" }} onClick={() => removeTeamRow(i)}>×</button>
              </div>
            ))}
          </div>

          <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={addJob} disabled={!canSave}>{saving ? "Saving…" : `Save ${newJob.Status} Job`}</button>
        </div>
      )}

      {/* Pipeline jobs: toggle which ones appear on the timeline. */}
      {prospects.length > 0 && (
        <div className="gantt-pipeline-strip">
          <span className="gantt-strip-label">Pipeline</span>
          {prospects.map(p => {
            const on = !!enabledProspects[p.Id];
            return (
              <button key={p.Id} className={`gantt-prospect-chip ${on ? "on" : ""}`} onClick={() => toggleProspect(p.Id)} title={on ? "Showing on the timeline — click to hide" : "Hidden — click to show on the timeline"}>
                <span className="gantt-prospect-dot" style={{ background: on ? confidenceColor(p.Confidence) : "var(--border-mid)" }} />
                <span className="gantt-prospect-name">{p.JobName}</span>
                <span className="gantt-prospect-conf">{p.Confidence}%</span>
              </button>
            );
          })}
          {activeProspects.length < prospects.length
            ? <button className="gantt-strip-link" onClick={enableAll}>Show all</button>
            : <button className="gantt-strip-link" onClick={() => { setProspectsTouched(true); setEnabledProspects({}); }}>Hide all</button>}
        </div>
      )}

      <div ref={presentRef} className={presenting ? "gantt-present" : ""}>
      {presenting && (
        <div className="gantt-present-bar">
          <div>
            <div className="gantt-present-title">Hardaway — Staffing Timeline</div>
            <div className="gantt-present-sub">{view === "project" ? "By project" : "By person"} · {todayLabel}{activeProspects.length ? ` · ${activeProspects.length} pipeline job${activeProspects.length === 1 ? "" : "s"} shown` : ""}</div>
          </div>
          <div className="gantt-toolbar-group">
            <div className="seg">
              <button className={view === "project" ? "on" : ""} onClick={() => setView("project")}>By Project</button>
              <button className={view === "person" ? "on" : ""} onClick={() => setView("person")}>By Person</button>
            </div>
            {view === "project" && <button className="btn btn-ghost" onClick={() => setExpandAll(s => !s)}><Icon name={expandAll ? "chevrons-up" : "chevrons-down"} size={14} /> {expandAll ? "Collapse all" : "Expand all"}</button>}
            <button className="btn btn-primary" onClick={stopPresenting}><Icon name="x" size={14} /> Exit</button>
          </div>
        </div>
      )}
      <div className="gantt-wrap" style={{ overflow: "auto" }} ref={ganttScrollRef}>
        <div style={{ minWidth: 270 + months.length * 52, position: "relative" }}>
          {todayTrackPct != null && (
            <div className="gantt-today-layer" aria-hidden="true">
              <div className="gantt-row-label" style={{ visibility: "hidden" }} />
              <div className="gantt-track-area">
                <div className="gantt-today" style={{ left: `${todayTrackPct}%` }} />
              </div>
            </div>
          )}
        <div className="gantt-header">
          <div className="gantt-row-label gantt-header-label">
            <span>{view === "project" ? "Project" : "Person"}</span>
            {view === "project" && !presenting && (
              <button className="gantt-strip-link" onClick={() => setExpandAll(s => !s)}>{expandAll ? "Collapse all" : "Expand all"}</button>
            )}
          </div>
          <div className="gantt-track-area">
            {months.map((m, i) => (
              <div key={i} className="gantt-month" style={{ width: `${100 / months.length}%` }}>
                {monthLabel(m)}{m.getMonth() === 0 ? ` '${String(m.getFullYear()).slice(2)}` : ""}
              </div>
            ))}
            {todayTrackPct != null && <div className="gantt-today-flag" style={{ left: `${todayTrackPct}%` }} title={`Data date: ${todayLabel}`}>▼ {todayLabel}</div>}
          </div>
        </div>

        {/* BY PROJECT */}
        {view === "project" && activeProjects.length === 0 && activeProspects.length === 0 && (
          <div className="empty" style={{ padding: 40 }}>
            <div className="empty-icon"><Icon name="calendar" /></div>
            <div className="empty-text">No active jobs with dates yet. Add a job above, or toggle a pipeline job.</div>
          </div>
        )}

        {view === "project" && activeProjects.map(p => {
          const pos = posOf(p.StartDate, p.CompletionDate);
          if (!pos) return null;
          const members = allMembers.filter(m => m.ProjectJobNumber === p.Title);
          const open = isOpen(p.Title);
          return (
            <React.Fragment key={p.Title}>
              <div className="gantt-row" onClick={() => toggleExpand(p.Title)} style={{ cursor: "pointer" }}>
                <div className="gantt-row-label">
                  <div className="gantt-row-name">{open ? "▾ " : "▸ "}{p.JobName}</div>
                  <div className="gantt-row-meta">{p.Title} · {members.filter(m => !isOpenSeat(m)).length} staffed · {p.BuildingType || "—"}{openSeatSummary(p.Title) && <span style={{ color: "var(--gold-text)", fontWeight: 700 }}> · needs {openSeatSummary(p.Title)}</span>}</div>
                  {!presenting && (
                    <button className="gantt-move-btn" onClick={e => { e.stopPropagation(); setMoveJob(p); }} title="Reschedule this job and its crew">
                      <Icon name="calendar" size={11} /> Move dates
                    </button>
                  )}
                </div>
                <div className="gantt-track-area">
                  {months.map((_, i) => <div key={i} className="gantt-gridline" style={{ left: `${(i / months.length) * 100}%` }} />)}
                  <div className="gantt-bar current" style={{ left: `${pos.left}%`, width: `${pos.width}%`, background: buildingColor(p.BuildingType) }} title={`${dateOnly(p.StartDate)} → ${dateOnly(p.CompletionDate)}`}>
                    <span className="gantt-bar-label">{p.JobName}</span>
                  </div>
                </div>
              </div>
              {open && memberRows(p.Title, p.StartDate, p.CompletionDate)}
            </React.Fragment>
          );
        })}

        {view === "project" && activeProspects.map(p => {
          const pos = posOf(p.StartDate, p.CompletionDate);
          if (!pos) return null;
          const open = isOpen(p.Title);
          const c = buildingColor(projType(p.Title));
          return (
            <React.Fragment key={p.Id}>
              <div className="gantt-row prospect" onClick={() => toggleExpand(p.Title)} style={{ cursor: "pointer" }}>
                <div className="gantt-row-label">
                  <div className="gantt-row-name">{open ? "▾ " : "▸ "}{p.JobName} <span className="gantt-prospect-badge">PIPELINE</span></div>
                  <div className="gantt-row-meta">{p.Confidence}% confidence{openSeatSummary(p.Title) ? <span style={{ color: "var(--gold-text)", fontWeight: 700 }}> · needs {openSeatSummary(p.Title)}</span> : ` · ${(p.RolesNeeded || []).length} roles`}</div>
                  {!presenting && (
                    <button className="gantt-move-btn" onClick={e => { e.stopPropagation(); setMoveJob(p); }} title="Reschedule this job and its crew">
                      <Icon name="calendar" size={11} /> Move dates
                    </button>
                  )}
                </div>
                <div className="gantt-track-area">
                  {months.map((_, i) => <div key={i} className="gantt-gridline" style={{ left: `${(i / months.length) * 100}%` }} />)}
                  <div className="gantt-bar prospect" style={{ left: `${pos.left}%`, width: `${pos.width}%`, background: `repeating-linear-gradient(45deg, ${c}, ${c} 6px, rgba(255,255,255,0.45) 6px, rgba(255,255,255,0.45) 12px)` }} title={`${dateOnly(p.StartDate)} → ${dateOnly(p.CompletionDate)}`}>
                    <span className="gantt-bar-label on-hatch">{p.JobName}</span>
                  </div>
                </div>
              </div>
              {open && memberRows(p.Title, p.StartDate, p.CompletionDate)}
            </React.Fragment>
          );
        })}

        {/* BY PERSON */}
        {view === "person" && onlyNoHome && unclassified.length > 0 && (
          <div className="gantt-unclassified">
            <Icon name="alert" size={14} />
            <span>
              {unclassified.length} active {unclassified.length === 1 ? "person has" : "people have"} no role set
              ({unclassified.map(p => p.FullName).join(", ")}), so they can't be forecast here.
              Set a role in <b>Admin → People</b>.
            </span>
          </div>
        )}

        {view === "person" && shownPeople.length === 0 && (
          <div className="empty" style={{ padding: 40 }}>
            <div className="empty-icon"><Icon name="users" /></div>
            <div className="empty-text">{onlyNoHome ? "✓ Everyone in operations has a job or prospective home through the next 90 days." : peopleWithWork.length === 0 ? "No one is staffed on the visible jobs. Toggle pipeline jobs or staff someone above." : "No one matches the current filter."}</div>
          </div>
        )}

        {view === "person" && shownPeople.map(({ person, items }) => {
          const lanes = Math.max(1, ...items.map(it => it.lane + 1));
          const rowH = lanes * 22 + 8;
          return (
            <div key={person.Id} className="gantt-row" style={{ minHeight: rowH }}>
              <div className="gantt-row-label">
                <div className="gantt-row-name">{person.FullName}</div>
                <div className="gantt-row-meta">{person.CompanyRole} · {items.length} job{items.length === 1 ? "" : "s"}{onlyNoHome && (() => { const h = homeInfo(person.FullName); return <span style={{ color: "var(--red)", fontWeight: 700 }}> · {h.latest ? `free after ${shortMDY(h.latest.toISOString())}` : "on bench now"}</span>; })()}</div>
              </div>
              <div className="gantt-track-area" style={{ height: rowH }}>
                {months.map((_, i) => <div key={i} className="gantt-gridline" style={{ left: `${(i / months.length) * 100}%` }} />)}
                {items.map((it, i) => {
                  const pos = posOf(it.s, it.e);
                  if (!pos) return null;
                  const c = projColor(it.title);
                  const bg = it.potential
                    ? `repeating-linear-gradient(45deg, ${c}, ${c} 6px, rgba(255,255,255,0.5) 6px, rgba(255,255,255,0.5) 12px)`
                    : c;
                  return (
                    <div key={i} className="gantt-bar" style={{ left: `${pos.left}%`, width: `${pos.width}%`, background: bg, height: 18, top: 4 + it.lane * 22 }} title={`${it.jobName}${it.potential ? " (pipeline)" : ""}: ${dateOnly(it.s)} → ${dateOnly(it.e)} · ${it.role}${it.alloc != null ? " · " + it.alloc + "%" : ""}`}>
                      <span className={`gantt-bar-label${it.potential ? " on-hatch" : ""}`} style={{ fontSize: 10 }}>{it.jobName}{it.alloc != null ? ` · ${it.alloc}%` : ""}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        </div>
      </div>
      </div>

      {/* Legend + reading hint, kept out of the way under the timeline. */}
      <div className="gantt-legend">
        {BUILDING_TYPES.map(t => (
          <span key={t}><span className="gantt-legend-swatch" style={{ background: buildingColor(t) }} /> {t}</span>
        ))}
        <span><span className="gantt-legend-swatch" style={{ background: "repeating-linear-gradient(45deg, #8a9099, #8a9099 4px, #fff 4px, #fff 8px)" }} /> Pipeline</span>
        <span className="gantt-legend-hint">{view === "project" ? "Bar color = building type · click a job to show its team" : "Each person's work across active and pipeline jobs"}</span>
      </div>

      {/* Unstaffed jobs — its own block between the planner and the projection. */}
      {unstaffedJobs.length > 0 && (
        <div className="card" style={{ borderLeft: "4px solid var(--red)", marginTop: 18 }}>
          <div className="card-header">
            <div className="card-title" style={{ color: "var(--red)" }}>
              <Icon name="alert" size={16} style={{ verticalAlign: "middle", marginRight: 6 }} />
              Jobs with No One Assigned · {unstaffedJobs.length}
            </div>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Open seats don't count as staffing</div>
          </div>
          {unstaffedJobs.map(job => {
            const openRoles = openSeatRoles(job.Title);
            const pipeline = job.Status === "Potential";
            return (
              <div key={job.Title} style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
                <div style={{ flex: "1 1 260px", minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>
                    {job.JobName || job.Title}
                    {pipeline && <span className="gantt-prospect-badge" style={{ marginLeft: 8 }}>PIPELINE</span>}
                  </div>
                  <div className="fin-job-meta" style={{ marginTop: 4 }}>
                    <span className="fin-job-num">{job.Title}</span>
                    {job.StartDate && <span style={{ marginLeft: 8, color: "var(--text-muted)" }}>{shortMDY(job.StartDate)} → {shortMDY(job.CompletionDate)}</span>}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".5px" }}>
                    {openRoles.length ? "Open positions" : ""}
                  </span>
                  {openRoles.length ? openRoles.map(({ role, count }) => {
                    const g = staffGroup(role);
                    return (
                      <span key={role} title={`${count} open ${role}`}
                        style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 800,
                          color: g.color, background: "#fff", border: `1.5px dashed ${g.color}`,
                          borderRadius: 999, padding: "3px 10px", whiteSpace: "nowrap" }}>
                        {count > 1 && <span>{count} ×</span>}{role}
                      </span>
                    );
                  }) : (
                    <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)" }}>No roles declared yet</span>
                  )}
                </div>
              </div>
            );
          })}
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 10 }}>
            Active and pipeline jobs that have no real person on them yet — a seat marked “Open” still counts as unstaffed.
          </div>
        </div>
      )}

      {activeProspects.length > 0 && (
        <div className="gantt-projection">
          <div className="gantt-proj-header">
            <Icon name="chart" size={18} />
            <div>
              <div className="gantt-proj-title">Personnel Projection</div>
              <div className="gantt-proj-sub">
                {totalNewRoles === 0
                  ? `None of the ${activeProspects.length} enabled pipeline job${activeProspects.length === 1 ? " has" : "s have"} declared what they need yet — add open seats to see the staffing gap.`
                  : <>Winning the {jobsWithNeeds} pipeline job{jobsWithNeeds === 1 ? "" : "s"} that {jobsWithNeeds === 1 ? "has" : "have"} declared roles would take {totalNewRoles} {totalNewRoles === 1 ? "person" : "people"}. Availability counts anyone not committed to an active job today.{activeProspects.length > jobsWithNeeds && ` (${activeProspects.length - jobsWithNeeds} other enabled job${activeProspects.length - jobsWithNeeds === 1 ? " hasn't" : "s haven't"} declared roles.)`}</>}
              </div>
            </div>
          </div>
          {overlapWarnings.length > 0 && (
            <div className="gantt-overlap-warn">
              <Icon name="alert" size={15} />
              <span><b>Simultaneous staffing:</b> {overlapWarnings.slice(0, 2).map((w, i) => (<span key={i}>{w[0]} + {w[1]}{i < Math.min(overlapWarnings.length, 2) - 1 ? "; " : ""}</span>))} overlap in time — you'll need the people at once.</span>
            </div>
          )}
          <div className="gantt-demand-grid">
            {shortfalls.map(s => (
              <div key={s.family} className={`gantt-demand-card ${s.gap > 0 ? "short" : "ok"}`}>
                <div className="gantt-demand-role">{s.label}</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 6 }}>{s.detail}</div>
                <div className="gantt-demand-nums">
                  <span className="gantt-demand-need">{s.need} needed</span>
                  <span className="gantt-demand-have">{s.have} available</span>
                </div>
                {s.gap > 0
                  ? <div className="gantt-demand-gap short">Hire {s.gap}</div>
                  : <div className="gantt-demand-gap ok">Covered</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {moveJob && (
        <MoveJobWizard
          project={moveJob}
          members={allMembers.filter(m => m.ProjectJobNumber === moveJob.Title)}
          onCancel={() => setMoveJob(null)}
          onApply={async ({ StartDate, EndDate, memberUpdates }) => {
            await moveJobAndCrew(moveJob.Id, {
              StartDate: StartDate ? new Date(StartDate).toISOString() : null,
              CompletionDate: EndDate ? new Date(EndDate).toISOString() : null,
            }, memberUpdates);
            setMoveJob(null);
          }}
        />
      )}
    </div>
  );
}
